import React from 'react'
import { useSelector } from 'react-redux'
import {
  Route,
  Routes,
  Navigate,
  unstable_HistoryRouter as HistoryRouter,
} from 'react-router-dom'
import { history } from '../router/navigation'

import { createTheme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'

import FormLayout from './layouts/FormLayout'
import MigratingFormLayout from './layouts/MigratingFormLayout'
import PageLayout from './layouts/PageLayout'
import Page from '../redux/features/pages/Page'
import LastPage from '../redux/features/pages/LastPage'
import PageDashboard from './PageDashboard'
import Registration from '../redux/features/user/Registration'
import EditPassword from '../redux/features/user/EditPassword'
import DeleteAccount from '../redux/features/user/DeleteAccount'
import EditEmail from '../redux/features/user/EditEmail'
import SignUp from '../redux/features/user/SignUp'
import MigrateOpenidAccount from './devise/MigrateOpenidAccount'
import VerifyAccount from './devise/VerifyAccount'
import ConfirmationInstructions from './devise/ConfirmationInstructions'
import ArticleIndex from './articles/ArticleIndex'
import Article from './articles/Article'
import NotFound from './NotFound'
import { selectCurrentUser } from '../redux/features/user/userSlice'

const theme = createTheme()

export default function AuthenticatedRoutes() {
  const currentUser = useSelector(selectCurrentUser)

  // 0.6.27.13: signed in via OpenID, but not confirmed yet -- covers both
  // "no login at all" (first visit) and "has a login from an earlier
  // claim attempt, but hasn't confirmed" (came back to fix a mistake,
  // e.g. a mistyped email, by re-authenticating at the OpenID provider
  // again -- found by testing, not assumed, that #claim signs the
  // account out on its own as soon as it has a login, so re-auth, not a
  // lingering session, is what gets back here; see User#
  // active_for_authentication? and the omniauth callback). Found
  // 2026-10-14: this has to block *everything* else, account included --
  // showing the normal signed-in nav and a real, editable account page
  // implied this account was fully set up when it wasn't. So every route
  // but the migrate screen itself (and the one it hands off to,
  // confirmation_instructions) redirects there, and both of those get
  // MigratingFormLayout's minimal chrome (logo + Sign Out only) instead
  // of FormLayout's normal AuthenticatedNavigation.
  // 0.6.27.16: a version-1 account that hasn't verified yet. Checked
  // before needsToClaimAccount below: confirmation_required? (and so
  // `confirmed`) doesn't even apply until a version-1 account verifies,
  // so checking `confirmed` first would wrongly send an old-but-never-
  // confirmed version-1 LocalUser to the OpenID claim screen instead.
  // `needs_version_migration`, not the compound `verified` -- `verified`
  // is also false for the pre-existing no-login OpenID population
  // (missing login/email, not behind on version), which would otherwise
  // get caught here too and misrouted to this screen instead of
  // `needsToClaimAccount`'s.
  const needsVerification = currentUser && currentUser.needs_version_migration
  const needsToClaimAccount = !needsVerification && currentUser && !currentUser.confirmed
  const blockedRedirect = needsVerification
    ? '/app/verify_account'
    : needsToClaimAccount
      ? '/app/migrate_openid_account'
      : null

  return (
    <ThemeProvider theme={theme}>
      <React.Fragment>
        <CssBaseline />
        <HistoryRouter
          history={history}
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <Routes>

            {/* Not verified/confirmed yet -- minimal chrome, no Account */}
            <Route path='/app' element={<MigratingFormLayout />} >
              <Route path='verify_account' element={<VerifyAccount />} />
              <Route path='migrate_openid_account' element={<MigrateOpenidAccount />} />
              <Route path='confirmation_instructions' element={<ConfirmationInstructions />} />
            </Route>

            {/* Form pages -- blocked until the account is verified and confirmed */}
            <Route path='/app' element={<FormLayout />} >
              <Route path='account'
                element={blockedRedirect
                  ? <Navigate to={blockedRedirect} replace />
                  : <Registration />} />
              <Route path='account/edit_email'
                element={blockedRedirect
                  ? <Navigate to={blockedRedirect} replace />
                  : <EditEmail />} />
              <Route path='account/edit_password'
                element={blockedRedirect
                  ? <Navigate to={blockedRedirect} replace />
                  : <EditPassword />} />
              <Route path='account/delete'
                element={blockedRedirect
                  ? <Navigate to={blockedRedirect} replace />
                  : <DeleteAccount />} />

              {/* The account page used to be at /app/user */}
              <Route path='user' element={<Navigate to="/app/account" replace />} />
              <Route path='user/edit_email'
                element={<Navigate to="/app/account/edit_email" replace />} />
              <Route path='user/edit_password'
                element={<Navigate to="/app/account/edit_password" replace />} />
            </Route>

            {/* News: Markdown files in articles/news (ArticlesController) */}
            <Route path='/news' element={<FormLayout />}>
              <Route index element={<ArticleIndex collection='news' title='Stikis News' />} />
              <Route path=':slug' element={<Article collection='news' collectionTitle='News' />} />
            </Route>
            {/* Help: Markdown files in articles/help (ArticlesController) */}
            <Route path='/help' element={<FormLayout />}>
              <Route index element={<ArticleIndex collection='help' title='Stikis Help' showDates={false} />} />
              <Route path=':slug' element={<Article collection='help' collectionTitle='Help pages' showDate={false} />} />
            </Route>

            {/* Page pages -- blocked until the account is verified and confirmed */}
            <Route path='/app' element={<PageLayout />} >
              <Route path='pages'
                element={blockedRedirect
                  ? <Navigate to={blockedRedirect} replace />
                  : <LastPage />} />
              <Route path='pages/:id'
                element={blockedRedirect
                  ? <Navigate to={blockedRedirect} replace />
                  : <Page />} />
              {/* Plain /app (and anything unknown): back to the last page,
                  without opening the pages drawer. */}
              <Route index
                element={blockedRedirect
                  ? <Navigate to={blockedRedirect} replace />
                  : <LastPage openPagesPane={false} />} />
              <Route path='*'
                element={blockedRedirect
                  ? <Navigate to={blockedRedirect} replace />
                  : <LastPage openPagesPane={false} />} />
            </Route>

            {/* Any other address (ErrorsController) */}
            <Route element={<FormLayout />}>
              <Route path='*' element={<NotFound />} />
            </Route>

          </Routes>
        </HistoryRouter>
      </React.Fragment>
    </ThemeProvider>
  )
}
